import { Group, Text, Box, Collapse, MultiSelect } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { Coding } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { APPOINTMENT_TYPES_SYSTEM, PROCEDURE_APPOINTMENT_CODES, ProviderRole } from '@bariendo/lib/constants';
import { expandCodeSystem } from '@bariendo/lib/codesystem';
import { TimeRange } from '@/pages/SchedulingPage/Availability/types';
import { useSchedulingPageContext } from '@/pages/SchedulingPage/SchedulingPageContext';
import { isProviderRoleAllowedForActivityDefinition } from '@bariendo/lib/scheduling';

interface AppointmentTypesInputProps {
  timeRange: TimeRange;
  onChange: (appointmentTypes: Coding[]) => void;
  collapsible?: boolean;
  selectAllByDefault?: boolean;
  providerRoleCode?: ProviderRole;
  /** When true, restrict the selectable options to bariatric procedure appointment types only. */
  proceduresOnly?: boolean;
}

/**
 * This component should be on SchedulingPageContext to avoid fetching appointment types everytime it is rendered.
 */
export function AppointmentTypesInput(props: AppointmentTypesInputProps) {
  const {
    timeRange,
    onChange,
    collapsible = false,
    selectAllByDefault = false,
    providerRoleCode,
    proceduresOnly = false,
  } = props;
  const medplum = useMedplum();
  const { appointmentTypeCodings, getActivityDefinitionForAppointmentType } = useSchedulingPageContext();
  const [fetchedAppointmentTypes, setFetchedAppointmentTypes] = useState<Coding[] | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const lastAutoApplied = useRef<{ roleKey: string | null; codes: string[] }>({ roleKey: null, codes: [] });

  const appointmentTypes: Coding[] | null =
    (appointmentTypeCodings && appointmentTypeCodings.length > 0 && appointmentTypeCodings) || fetchedAppointmentTypes;

  useEffect(() => {
    if (appointmentTypeCodings && appointmentTypeCodings.length > 0) {
      return;
    }

    async function loadAppointmentTypes() {
      const codings = await expandCodeSystem(medplum, APPOINTMENT_TYPES_SYSTEM);
      setFetchedAppointmentTypes(codings);
    }

    loadAppointmentTypes();
  }, [medplum, appointmentTypeCodings]);

  const filteredAppointmentTypes: Coding[] | null = useMemo(() => {
    if (!appointmentTypes) {
      return null;
    }
    return appointmentTypes.filter((coding) => {
      if (proceduresOnly && !(PROCEDURE_APPOINTMENT_CODES as readonly string[]).includes(coding.code ?? '')) {
        return false;
      }

      if (!providerRoleCode) {
        return true;
      }

      const code = coding.code;
      if (!code) {
        return true;
      }

      const activityDefinition = getActivityDefinitionForAppointmentType(code);
      if (!activityDefinition) {
        return true;
      }

      // Overflow types are offered, so a provider can be given one on purpose (BAR-3101).
      return isProviderRoleAllowedForActivityDefinition(activityDefinition, providerRoleCode, {
        includeOverflow: true,
      });
    });
  }, [appointmentTypes, providerRoleCode, getActivityDefinitionForAppointmentType, proceduresOnly]);

  // What "select all by default" selects: every offered type except the ones this
  // role may only take as overflow. An NP's new range should not claim the PN
  // consultation unless someone picks it.
  const defaultAppointmentTypes: Coding[] | null = useMemo(() => {
    if (!filteredAppointmentTypes || !providerRoleCode) {
      return filteredAppointmentTypes;
    }
    return filteredAppointmentTypes.filter((coding) => {
      const activityDefinition = coding.code ? getActivityDefinitionForAppointmentType(coding.code) : undefined;
      return !activityDefinition || isProviderRoleAllowedForActivityDefinition(activityDefinition, providerRoleCode);
    });
  }, [filteredAppointmentTypes, providerRoleCode, getActivityDefinitionForAppointmentType]);

  // Apply or re-apply "select all by default" when:
  // - The time range has no appointment types yet, or
  // - The current selection still matches the last auto-applied list and the provider role changed.
  useEffect(() => {
    if (!selectAllByDefault || !defaultAppointmentTypes || defaultAppointmentTypes.length === 0) {
      return;
    }

    const currentCodes = timeRange.appointmentTypes
      .map((coding) => coding.code)
      .filter((code): code is string => !!code);
    const desiredCodes = defaultAppointmentTypes.map((coding) => coding.code).filter((code): code is string => !!code);

    const roleKey = providerRoleCode ?? 'none';

    const arraysEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((value, index) => value === b[index]);

    const wasAutoApplied =
      lastAutoApplied.current.codes.length > 0 && arraysEqual(currentCodes, lastAutoApplied.current.codes);

    const roleChanged = lastAutoApplied.current.roleKey !== roleKey;

    const shouldApplyForEmpty = currentCodes.length === 0 && lastAutoApplied.current.codes.length === 0;
    const shouldReapplyForRoleChange = wasAutoApplied && roleChanged;

    if (shouldApplyForEmpty || shouldReapplyForRoleChange) {
      lastAutoApplied.current = { roleKey, codes: desiredCodes };
      onChange(defaultAppointmentTypes);
    }
  }, [selectAllByDefault, defaultAppointmentTypes, timeRange.appointmentTypes, providerRoleCode, onChange]);

  const selectData =
    filteredAppointmentTypes?.map((coding) => ({
      value: coding.code!,
      label: coding.display as string,
    })) ?? [];

  const selectedValues = timeRange.appointmentTypes.map((coding) => coding.code!).filter(Boolean);

  const handleChange = (selectedCodes: string[]) => {
    const selectedCodings = selectedCodes
      .map((code) => filteredAppointmentTypes?.find((coding) => coding.code === code))
      .filter((coding): coding is Coding => coding !== undefined);
    onChange(selectedCodings);
  };

  if (!collapsible) {
    return (
      <MultiSelect
        label="Appointment types"
        data={selectData}
        value={selectedValues}
        onChange={handleChange}
        placeholder="Select appointment types"
        required
        searchable
        clearable
      />
    );
  }

  return (
    <>
      <Group
        gap="xs"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
        mb={isExpanded ? 'xs' : 0}
        wrap="nowrap"
      >
        {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        <Text size="sm" fw={500}>
          Appointment types <span style={{ color: 'red' }}>*</span>
        </Text>
      </Group>
      <Collapse in={isExpanded}>
        <Box mt="xs">
          <MultiSelect
            data={selectData}
            value={selectedValues}
            onChange={handleChange}
            placeholder="Select appointment types"
            searchable
            clearable
          />
        </Box>
      </Collapse>
    </>
  );
}
